import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { 
  Building2, 
  Plus, 
  Search, 
  Filter, 
  ExternalLink, 
  Edit3, 
  Trash2, 
  X, 
  CheckCircle2, 
  BookOpen, 
  Handshake, 
  Globe2, 
  DollarSign,
  Layers,
  Sparkles
} from 'lucide-react';
import { PartnerRecord, PartnerType, PartnerRelationshipStage } from '@ftt/shared';
import { PARTNER_TYPES_CONFIG } from '@ftt/shared';

export const AdminPartnersPage: React.FC = () => {
  const [selectedType, setSelectedType] = useState<string>('ALL');
  const [selectedStage, setSelectedStage] = useState<string>('ALL');
  const [searchQuery, setSearchQuery] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingPartner, setEditingPartner] = useState<PartnerRecord | null>(null);

  // Form State
  const [name, setName] = useState('');
  const [partnerType, setPartnerType] = useState<PartnerType>('PUBLISHER_RIGHTS');
  const [contactPerson, setContactPerson] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [website, setWebsite] = useState('');
  const [country, setCountry] = useState('India');
  const [city, setCity] = useState('');
  const [relationshipStage, setRelationshipStage] = useState<PartnerRelationshipStage>('ACTIVE_PARTNER');
  const [contractStartDate, setContractStartDate] = useState('');
  const [contractEndDate, setContractEndDate] = useState('');
  const [royaltyAgreementTerms, setRoyaltyAgreementTerms] = useState('');
  const [licensedTitlesCount, setLicensedTitlesCount] = useState(0);
  const [activeProjectsCount, setActiveProjectsCount] = useState(0);
  const [notes, setNotes] = useState('');
  const [assignedStaffName, setAssignedStaffName] = useState('Ashish B.');

  const queryClient = useQueryClient();

  const { data: partnersRes, isLoading } = useQuery({
    queryKey: ['admin-partners', { type: selectedType, stage: selectedStage, search: searchQuery }],
    queryFn: () => {
      let url = '/admin/partners';
      const params = new URLSearchParams();
      if (selectedType !== 'ALL') params.append('type', selectedType);
      if (selectedStage !== 'ALL') params.append('stage', selectedStage);
      if (searchQuery) params.append('search', searchQuery);
      if (params.toString()) url += `?${params.toString()}`;
      return api.get(url).then((res) => res.data.data);
    },
  });

  const partners: PartnerRecord[] = partnersRes || [];

  const saveMutation = useMutation({
    mutationFn: async () => {
      const payload = {
        name,
        partnerType,
        contactPerson,
        email,
        phone: phone || null,
        website: website || null,
        country,
        city: city || null,
        relationshipStage,
        contractStartDate: contractStartDate || new Date().toISOString(),
        contractEndDate: contractEndDate || null,
        royaltyAgreementTerms: royaltyAgreementTerms || null,
        licensedTitlesCount,
        activeProjectsCount,
        notes: notes || null,
        assignedStaffName: assignedStaffName || null,
      };

      if (editingPartner?.id) {
        return api.put(`/admin/partners/${editingPartner.id}`, payload);
      } else {
        return api.post('/admin/partners', payload);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-partners'] });
      setIsModalOpen(false);
      resetForm();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/admin/partners/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-partners'] });
    },
  });

  const resetForm = () => {
    setEditingPartner(null);
    setName('');
    setPartnerType('PUBLISHER_RIGHTS');
    setContactPerson('');
    setEmail('');
    setPhone('');
    setWebsite('');
    setCountry('India');
    setCity('');
    setRelationshipStage('ACTIVE_PARTNER');
    setContractStartDate('');
    setContractEndDate('');
    setRoyaltyAgreementTerms('');
    setLicensedTitlesCount(0);
    setActiveProjectsCount(0);
    setNotes('');
    setAssignedStaffName('Ashish B.');
  };

  const handleOpenEdit = (partner: PartnerRecord) => {
    setEditingPartner(partner);
    setName(partner.name);
    setPartnerType(partner.partnerType);
    setContactPerson(partner.contactPerson);
    setEmail(partner.email);
    setPhone(partner.phone || '');
    setWebsite(partner.website || '');
    setCountry(partner.country);
    setCity(partner.city || '');
    setRelationshipStage(partner.relationshipStage);
    setContractStartDate(partner.contractStartDate ? partner.contractStartDate.split('T')[0] : '');
    setContractEndDate(partner.contractEndDate ? partner.contractEndDate.split('T')[0] : '');
    setRoyaltyAgreementTerms(partner.royaltyAgreementTerms || '');
    setLicensedTitlesCount(partner.licensedTitlesCount);
    setActiveProjectsCount(partner.activeProjectsCount);
    setNotes(partner.notes || '');
    setAssignedStaffName(partner.assignedStaffName || 'Ashish B.');
    setIsModalOpen(true);
  };

  // Metrics
  const totalPartners = partners.length;
  const rightsPartners = partners.filter((p) => p.partnerType === 'PUBLISHER_RIGHTS').length;
  const distributionPartners = partners.filter((p) => p.partnerType === 'DISTRIBUTION').length;
  const totalLicensedTitles = partners.reduce((sum, p) => sum + p.licensedTitlesCount, 0);

  return (
    <div className="space-y-6 font-sans text-slate-900 antialiased">
      
      {/* 1. Header & Primary Action */}
      <div className="bg-white rounded-2xl md:rounded-3xl p-6 shadow-sm border border-slate-200/90 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <span className="text-[10px] text-brand-700 font-mono uppercase tracking-widest font-bold">
              CRM &amp; STRATEGIC ALLIANCES
            </span>
            <span className="text-slate-400 font-mono text-xs">&bull;</span>
            <span className="text-xs text-slate-600 font-serif italic">
              FOR THE TRUTH PRESS PRIVATE LIMITED
            </span>
          </div>
          <h1 className="text-2xl sm:text-3xl font-serif font-black text-slate-900 flex items-center gap-2.5 mt-1">
            <Handshake className="w-7 h-7 text-[#FF6B4A]" />
            <span>Partner Organizations &amp; Publishers</span>
          </h1>
          <p className="text-xs sm:text-sm text-slate-600 font-medium mt-1">
            Manage Publishing Rights holders, distribution networks, ecclesiastical partnerships, and translation consortiums.
          </p>
        </div>

        <button
          onClick={() => {
            resetForm();
            setIsModalOpen(true);
          }}
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-slate-900 hover:bg-slate-800 text-white font-extrabold text-xs shadow-md transition-all shrink-0 cursor-pointer"
        >
          <Plus className="w-4 h-4 text-[#FF6B4A]" />
          <span>Add New Partner</span>
        </button>
      </div>

      {/* 2. Metric Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="p-5 rounded-2xl bg-white border border-slate-200/90 shadow-sm space-y-1">
          <span className="text-[10px] font-mono uppercase tracking-wider text-slate-600 font-bold">Total Partners</span>
          <div className="text-2xl font-black text-slate-900 font-serif">{totalPartners}</div>
          <span className="text-[11px] text-slate-600 font-bold">Across 4 countries</span>
        </div>

        <div className="p-5 rounded-2xl bg-white border border-slate-200/90 shadow-sm space-y-1">
          <span className="text-[10px] font-mono uppercase tracking-wider text-brand-700 font-bold">Rights Publishers</span>
          <div className="text-2xl font-black text-brand-700 font-serif">{rightsPartners}</div>
          <span className="text-[11px] text-slate-600 font-bold">Crossway, IVP, Banner, Good Book</span>
        </div>

        <div className="p-5 rounded-2xl bg-white border border-slate-200/90 shadow-sm space-y-1">
          <span className="text-[10px] font-mono uppercase tracking-wider text-emerald-700 font-bold">Distribution &amp; Stores</span>
          <div className="text-2xl font-black text-emerald-700 font-serif">{distributionPartners}</div>
          <span className="text-[11px] text-slate-600 font-bold">forthetruth.in &amp; seminaries</span>
        </div>

        <div className="p-5 rounded-2xl bg-white border border-slate-200/90 shadow-sm space-y-1">
          <span className="text-[10px] font-mono uppercase tracking-wider text-purple-700 font-bold">Licensed Titles</span>
          <div className="text-2xl font-black text-purple-700 font-serif">{totalLicensedTitles}</div>
          <span className="text-[11px] text-slate-600 font-bold">Indian language translations</span>
        </div>
      </div>

      {/* 3. Filter and Search Bar */}
      <div className="p-4 rounded-2xl bg-white border border-slate-200/90 shadow-sm flex flex-wrap items-center justify-between gap-4">
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative">
            <Search className="w-3.5 h-3.5 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              placeholder="Search partner or contact..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 pr-4 py-2 rounded-xl bg-slate-50 border border-slate-200 text-xs text-slate-900 font-semibold placeholder-slate-400 focus:outline-hidden focus:ring-2 focus:ring-brand-500 w-56 sm:w-64"
            />
          </div>

          <div className="flex items-center gap-2">
            <span className="text-xs text-slate-600 font-bold">Type:</span>
            <select
              value={selectedType}
              onChange={(e) => setSelectedType(e.target.value)}
              className="px-3 py-2 rounded-xl bg-slate-50 border border-slate-200 text-xs text-slate-800 font-bold focus:outline-hidden cursor-pointer"
            >
              <option value="ALL">All Partner Types</option>
              <option value="PUBLISHER_RIGHTS">Publishing Rights Partner</option>
              <option value="DISTRIBUTION">Distribution &amp; Bookstore</option>
              <option value="CHURCH_MINISTRY">Church &amp; Ministry</option>
              <option value="TRANSLATION_ORGANIZATION">Translation Consortium</option>
              <option value="ACADEMIC_INSTITUTION">Academic &amp; Seminary</option>
            </select>
          </div>
        </div>

        <div className="text-xs text-slate-600 font-mono font-bold">
          Showing <strong className="text-slate-900">{partners.length}</strong> partners
        </div>
      </div>

      {/* 4. Partners Grid */}
      {isLoading ? (
        <div className="py-16 text-center text-xs font-mono text-slate-500 bg-white rounded-2xl border border-slate-200">Loading partner database...</div>
      ) : partners.length === 0 ? (
        <div className="py-16 text-center text-sm text-slate-500 bg-white rounded-2xl border border-slate-200">No partner records found matching your filters.</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {partners.map((partner) => (
            <div
              key={partner.id}
              className="p-6 rounded-2xl md:rounded-3xl bg-white border border-slate-200/90 hover:border-brand-300 hover:shadow-md transition-all flex flex-col justify-between space-y-4 shadow-sm"
            >
              <div className="space-y-3">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <span className="text-[9px] font-mono uppercase px-2 py-0.5 rounded-md font-bold bg-brand-50 text-brand-800 border border-brand-200">
                      {partner.partnerType.replace(/_/g, ' ')}
                    </span>
                    <h3 className="font-serif text-lg font-black text-slate-900 pt-1.5 leading-snug">
                      {partner.name}
                    </h3>
                  </div>

                  <span className={`text-[10px] font-mono px-2 py-0.5 rounded-md font-bold ${
                    partner.relationshipStage === 'ACTIVE_PARTNER'
                      ? 'bg-emerald-50 text-emerald-800 border border-emerald-200'
                      : 'bg-amber-50 text-amber-800 border border-amber-200'
                  }`}>
                    {partner.relationshipStage.replace(/_/g, ' ')}
                  </span>
                </div>

                <div className="space-y-1.5 text-xs text-slate-600 font-sans font-medium">
                  <p><strong className="text-slate-900">Contact:</strong> {partner.contactPerson}</p>
                  <p><strong className="text-slate-900">Email:</strong> <a href={`mailto:${partner.email}`} className="text-brand-700 font-bold hover:underline">{partner.email}</a></p>
                  {partner.phone && <p><strong className="text-slate-900">Phone:</strong> {partner.phone}</p>}
                  <p><strong className="text-slate-900">Location:</strong> {partner.city ? `${partner.city}, ` : ''}{partner.country}</p>
                </div>

                {partner.royaltyAgreementTerms && (
                  <div className="p-3 rounded-xl bg-slate-50 border border-slate-200 text-[11px] text-slate-800 font-mono font-medium">
                    <span className="text-[9px] uppercase tracking-wider text-slate-500 font-bold block mb-0.5">Agreement Scope</span>
                    {partner.royaltyAgreementTerms}
                  </div>
                )}
              </div>

              <div className="pt-4 border-t border-slate-100 flex items-center justify-between text-xs font-mono">
                <div className="flex items-center gap-3 text-slate-600 font-bold">
                  <span><strong className="text-slate-900">{partner.licensedTitlesCount}</strong> Titles</span>
                  <span>&bull;</span>
                  <span><strong className="text-slate-900">{partner.activeProjectsCount}</strong> Active</span>
                </div>

                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handleOpenEdit(partner)}
                    className="p-1.5 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 hover:text-slate-900 transition-colors cursor-pointer"
                    title="Edit Partner"
                  >
                    <Edit3 className="w-3.5 h-3.5" />
                  </button>

                  <button
                    onClick={() => {
                      if (confirm(`Remove partner "${partner.name}"?`)) {
                        deleteMutation.mutate(partner.id);
                      }
                    }}
                    className="p-1.5 rounded-lg bg-rose-50 hover:bg-rose-100 text-rose-600 hover:text-rose-700 transition-colors cursor-pointer"
                    title="Delete Partner"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal for Add / Edit Partner */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-xs overflow-y-auto">
          <div className="bg-white border border-slate-200 rounded-3xl max-w-2xl w-full p-6 sm:p-8 space-y-6 shadow-2xl max-h-[90vh] overflow-y-auto text-slate-900">
            <div className="flex items-center justify-between border-b border-slate-100 pb-4">
              <h2 className="text-xl font-serif font-bold text-slate-900">
                {editingPartner ? 'Edit Partner Organization' : 'Add New Partner Organization'}
              </h2>
              <button
                onClick={() => setIsModalOpen(false)}
                className="p-1.5 rounded-lg text-slate-400 hover:text-slate-900 hover:bg-slate-100 cursor-pointer"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form
              onSubmit={(e) => {
                e.preventDefault();
                saveMutation.mutate();
              }}
              className="space-y-4 text-xs font-sans"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1">
                  <label className="text-slate-700 font-bold block">Organization / Publisher Name *</label>
                  <input
                    type="text"
                    required
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="e.g. Crossway Books"
                    className="w-full px-3.5 py-2 rounded-xl bg-slate-50 border border-slate-200 text-slate-900 font-semibold focus:outline-hidden"
                  />
                </div>

                <div className="space-y-1">
                  <label className="text-slate-700 font-bold block">Partner Type *</label>
                  <select
                    value={partnerType}
                    onChange={(e) => setPartnerType(e.target.value as PartnerType)}
                    className="w-full px-3.5 py-2 rounded-xl bg-slate-50 border border-slate-200 text-slate-900 font-semibold focus:outline-hidden"
                  >
                    <option value="PUBLISHER_RIGHTS">Publishing Rights Partner</option>
                    <option value="DISTRIBUTION">Distribution &amp; Bookstore</option>
                    <option value="CHURCH_MINISTRY">Church &amp; Ministry</option>
                    <option value="TRANSLATION_ORGANIZATION">Translation Consortium</option>
                    <option value="ACADEMIC_INSTITUTION">Academic &amp; Seminary</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1">
                  <label className="text-slate-700 font-bold block">Contact Person Name *</label>
                  <input
                    type="text"
                    required
                    value={contactPerson}
                    onChange={(e) => setContactPerson(e.target.value)}
                    placeholder="e.g. Dr. Justin Taylor"
                    className="w-full px-3.5 py-2 rounded-xl bg-slate-50 border border-slate-200 text-slate-900 font-semibold focus:outline-hidden"
                  />
                </div>

                <div className="space-y-1">
                  <label className="text-slate-700 font-bold block">Contact Email *</label>
                  <input
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="rights@crossway.org"
                    className="w-full px-3.5 py-2 rounded-xl bg-slate-50 border border-slate-200 text-slate-900 font-semibold focus:outline-hidden"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="space-y-1">
                  <label className="text-slate-700 font-bold block">Phone Number</label>
                  <input
                    type="text"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="+1 (630) 682-4300"
                    className="w-full px-3.5 py-2 rounded-xl bg-slate-50 border border-slate-200 text-slate-900 font-semibold focus:outline-hidden"
                  />
                </div>

                <div className="space-y-1">
                  <label className="text-slate-700 font-bold block">City</label>
                  <input
                    type="text"
                    value={city}
                    onChange={(e) => setCity(e.target.value)}
                    placeholder="Wheaton, IL"
                    className="w-full px-3.5 py-2 rounded-xl bg-slate-50 border border-slate-200 text-slate-900 font-semibold focus:outline-hidden"
                  />
                </div>

                <div className="space-y-1">
                  <label className="text-slate-700 font-bold block">Country *</label>
                  <input
                    type="text"
                    required
                    value={country}
                    onChange={(e) => setCountry(e.target.value)}
                    placeholder="United States"
                    className="w-full px-3.5 py-2 rounded-xl bg-slate-50 border border-slate-200 text-slate-900 font-semibold focus:outline-hidden"
                  />
                </div>
              </div>

              <div className="space-y-1">
                <label className="text-slate-700 font-bold block">Royalty &amp; Licensing Scope Terms</label>
                <textarea
                  rows={2}
                  value={royaltyAgreementTerms}
                  onChange={(e) => setRoyaltyAgreementTerms(e.target.value)}
                  placeholder="e.g. 8% Net royalty for South Asian print and vernacular translation licenses."
                  className="w-full px-3.5 py-2 rounded-xl bg-slate-50 border border-slate-200 text-slate-900 font-semibold focus:outline-hidden"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1">
                  <label className="text-slate-700 font-bold block">Licensed Titles Count</label>
                  <input
                    type="number"
                    value={licensedTitlesCount}
                    onChange={(e) => setLicensedTitlesCount(Number(e.target.value))}
                    className="w-full px-3.5 py-2 rounded-xl bg-slate-50 border border-slate-200 text-slate-900 font-semibold focus:outline-hidden"
                  />
                </div>

                <div className="space-y-1">
                  <label className="text-slate-700 font-bold block">Assigned Lead / Staff</label>
                  <input
                    type="text"
                    value={assignedStaffName}
                    onChange={(e) => setAssignedStaffName(e.target.value)}
                    className="w-full px-3.5 py-2 rounded-xl bg-slate-50 border border-slate-200 text-slate-900 font-semibold focus:outline-hidden"
                  />
                </div>
              </div>

              <div className="flex items-center justify-end gap-3 pt-4 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="px-4 py-2 rounded-xl border border-slate-200 text-slate-700 hover:bg-slate-100 font-bold cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saveMutation.isPending}
                  className="px-5 py-2 rounded-xl bg-slate-900 hover:bg-slate-800 text-white font-bold disabled:opacity-50 cursor-pointer shadow-sm"
                >
                  {saveMutation.isPending ? 'Saving...' : editingPartner ? 'Update Partner' : 'Save Partner Record'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  );
};
